import { useState, useEffect } from "react";
import axios from "axios";
import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "./Impianti.css";
import Lf23Loader from "../../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const FullscreenBtn = ({ isActive, onClick }) => (
    <button className="rom-fullscreen-btn" onClick={onClick} title={isActive ? 'Esci da tutto schermo' : 'Tutto schermo'}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {isActive ? (
                <><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" y1="10" x2="21" y2="3"></line><line x1="3" y1="21" x2="10" y2="14"></line></>
            ) : (
                <><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></>
            )}
        </svg>
    </button>
);

const Impianti = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fullscreen, setFullscreen] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API_URL}/report-orari-magazziniere/impianti`);
                setData(res.data);
            } catch (err) {
                console.error("Errore fetch impianti:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') setFullscreen(null); };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    const toggleFs = (key) => setFullscreen(prev => prev === key ? null : key);

    if (loading) return (
        <Lf23Loader />
    );
    if (!data) return null;

    const { kpi, profiloOrario, profiloSettimanale, tempoAttesa, dettaglioGiornaliero } = data;

    const maxCompletamenti = Math.max(...profiloOrario.map(r => r.completamenti), 0);
    const maxCreazioni = Math.max(...profiloOrario.map(r => r.creazioni), 0);
    const maxSettCompl = Math.max(...profiloSettimanale.map(r => r.completamenti), 0);
    const maxSettCreaz = Math.max(...profiloSettimanale.map(r => r.creazioni), 0);

    const tempoAttesaRows = [
        { metrica: "Media", valore: tempoAttesa.media },
        { metrica: "Mediana", valore: tempoAttesa.mediana },
        { metrica: "P90", valore: tempoAttesa.p90 },
        { metrica: "Minimo", valore: tempoAttesa.minimo },
        { metrica: "Massimo", valore: tempoAttesa.massimo },
    ];

    return (
        <div className="rom-impianti">
            {/* KPI PRINCIPALI */}
            <div className="kpi-strip-rom">
                <div className="kpi-card-rom kpi-blue">
                    <div className="kpi-icon-rom">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
                    </div>
                    <div className="kpi-info-rom">
                        <span className="kpi-value-rom">{Number(kpi.nr_totale).toLocaleString("it-IT")}</span>
                        <span className="kpi-label-rom">Ceste Svuotamenti (totale)</span>
                    </div>
                </div>
                <div className="kpi-card-rom kpi-green">
                    <div className="kpi-icon-rom">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v17M5.5 8H2l4-4 4 4H6.5M18.5 16H22l-4 4-4-4h3.5"></path></svg>
                    </div>
                    <div className="kpi-info-rom">
                        <span className="kpi-value-rom">{Number(kpi.peso_medio).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg</span>
                        <span className="kpi-label-rom">Peso Medio</span>
                    </div>
                </div>
                <div className="kpi-card-rom kpi-yellow">
                    <div className="kpi-icon-rom">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    </div>
                    <div className="kpi-info-rom">
                        <span className="kpi-value-rom">{Number(kpi.attesa_mediana).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h</span>
                        <span className="kpi-label-rom">Attesa Mediana</span>
                    </div>
                </div>
                <div className="kpi-card-rom kpi-pink">
                    <div className="kpi-icon-rom">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    </div>
                    <div className="kpi-info-rom">
                        <span className="kpi-value-rom">{Number(kpi.attesa_p90).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h</span>
                        <span className="kpi-label-rom">Attesa P90</span>
                    </div>
                </div>
                <div className="kpi-card-rom kpi-purple">
                    <div className="kpi-icon-rom">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>
                    </div>
                    <div className="kpi-info-rom">
                        <span className="kpi-value-rom">{Number(kpi.cadenza_mediana).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} min</span>
                        <span className="kpi-label-rom">Cadenza Mediana</span>
                    </div>
                </div>
                <div className="kpi-card-rom kpi-orange">
                    <div className="kpi-icon-rom">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>
                    </div>
                    <div className="kpi-info-rom">
                        <span className="kpi-value-rom">{Number(kpi.cadenza_p90).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} min</span>
                        <span className="kpi-label-rom">Cadenza P90</span>
                    </div>
                </div>
            </div>

            {/* PROFILO ORARIO */}
            <div className={`rom-impianti-section${fullscreen === 'orario' ? ' fullscreen-rom' : ''}`}>
                <RotateDevice active={fullscreen === 'orario'} />
                <div className="rom-impianti-section-header">
                    <h3 className="rom-impianti-section-title">Profilo Orario</h3>
                    <FullscreenBtn isActive={fullscreen === 'orario'} onClick={() => toggleFs('orario')} />
                </div>
                <p className="rom-impianti-section-desc">Distribuzione svuotamenti e creazioni per fascia oraria</p>

                <div className="rom-impianti-table-wrapper">
                    <table className="rom-impianti-table">
                        <thead>
                            <tr>
                                <th className="rom-impianti-th-ora">Ora</th>
                                <th>Svuotamenti</th>
                                <th>Creazioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {profiloOrario.map((row, i) => (
                                <tr key={i}>
                                    <td className="rom-impianti-td-ora">{row.ora}</td>
                                    <td>
                                        <div className="rom-impianti-bar-cell">
                                            <div
                                                className="rom-impianti-bar rom-impianti-bar-svuotamenti"
                                                style={{ width: maxCompletamenti > 0 ? `${(row.completamenti / maxCompletamenti) * 100}%` : "0%" }}
                                            >
                                                <span className="rom-impianti-bar-label">{row.completamenti}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="rom-impianti-bar-cell">
                                            <div
                                                className="rom-impianti-bar rom-impianti-bar-creazioni"
                                                style={{ width: maxCreazioni > 0 ? `${(row.creazioni / maxCreazioni) * 100}%` : "0%" }}
                                            >
                                                <span className="rom-impianti-bar-label">{row.creazioni}</span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* PROFILO SETTIMANALE */}
            <div className={`rom-impianti-section${fullscreen === 'settimanale' ? ' fullscreen-rom' : ''}`}>
                <RotateDevice active={fullscreen === 'settimanale'} />
                <div className="rom-impianti-section-header">
                    <h3 className="rom-impianti-section-title">Profilo Settimanale</h3>
                    <FullscreenBtn isActive={fullscreen === 'settimanale'} onClick={() => toggleFs('settimanale')} />
                </div>

                <div className="rom-impianti-table-wrapper">
                    <table className="rom-impianti-table">
                        <thead>
                            <tr>
                                <th>Giorno</th>
                                <th>Svuotamenti</th>
                                <th>Creazioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {profiloSettimanale.map((row, i) => (
                                <tr key={i}>
                                    <td className="rom-impianti-td-ora">{row.giorno}</td>
                                    <td>
                                        <div className="rom-impianti-bar-cell">
                                            <div
                                                className="rom-impianti-bar rom-impianti-bar-svuotamenti"
                                                style={{ width: maxSettCompl > 0 ? `${(row.completamenti / maxSettCompl) * 100}%` : "0%" }}
                                            >
                                                <span className="rom-impianti-bar-label">{row.completamenti}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="rom-impianti-bar-cell">
                                            <div
                                                className="rom-impianti-bar rom-impianti-bar-creazioni"
                                                style={{ width: maxSettCreaz > 0 ? `${(row.creazioni / maxSettCreaz) * 100}%` : "0%" }}
                                            >
                                                <span className="rom-impianti-bar-label">{row.creazioni}</span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* TEMPO DI ATTESA */}
            <div className={`rom-impianti-section${fullscreen === 'attesa' ? ' fullscreen-rom' : ''}`}>
                <RotateDevice active={fullscreen === 'attesa'} />
                <div className="rom-impianti-section-header">
                    <h3 className="rom-impianti-section-title">Tempo di Attesa</h3>
                    <FullscreenBtn isActive={fullscreen === 'attesa'} onClick={() => toggleFs('attesa')} />
                </div>
                <p className="rom-impianti-section-desc">Tempo tra creazione e svuotata (esclusi outlier &gt; 30gg)</p>

                <div className="rom-impianti-table-wrapper">
                    <table className="rom-impianti-table">
                        <thead>
                            <tr>
                                <th>Metrica</th>
                                <th>Valore (ore)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tempoAttesaRows.map((row, i) => (
                                <tr key={i}>
                                    <td>{row.metrica}</td>
                                    <td>{Number(row.valore).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* DETTAGLIO GIORNALIERO */}
            <div className={`rom-impianti-section${fullscreen === 'dettaglio' ? ' fullscreen-rom' : ''}`}>
                <RotateDevice active={fullscreen === 'dettaglio'} />
                <div className="rom-impianti-section-header">
                    <h3 className="rom-impianti-section-title">Dettaglio Giornaliero (ultimi 90 giorni)</h3>
                    <FullscreenBtn isActive={fullscreen === 'dettaglio'} onClick={() => toggleFs('dettaglio')} />
                </div>

                <div className="rom-impianti-table-wrapper">
                    <table className="rom-impianti-table">
                        <thead>
                            <tr>
                                <th>Giorno</th>
                                <th>Nr Ceste</th>
                                <th>Primo</th>
                                <th>Ultimo</th>
                                <th>Durata (ore)</th>
                                <th>Peso Tot (kg)</th>
                                <th>Peso Medio (kg)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dettaglioGiornaliero.map((row, i) => (
                                <tr key={i}>
                                    <td>{row.giorno}</td>
                                    <td>{row.nr_record}</td>
                                    <td>{row.primo}</td>
                                    <td>{row.ultimo}</td>
                                    <td>{Number(row.durata_ore).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                    <td>{Number(row.peso_totale).toLocaleString("it-IT")}</td>
                                    <td>{Number(row.peso_medio).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Impianti;
